/* ===================================================================
   CONSCIENCE — Kalpa Druma
   Shared behaviour for all conscience-* sections.
   Written to survive the Shopify theme editor: every init function is
   idempotent and re-runs on section:load.
   =================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DEFAULT_ACCENT = '#2C3E5D';

  /* ---------------- shared dye state ---------------- */
  var Conscience = window.Conscience || {};
  Conscience.activeDye = Conscience.activeDye || null;

  Conscience.setDye = function (name, hex) {
    Conscience.activeDye = name;
    document.documentElement.style.setProperty('--cn-accent', hex);

    document.querySelectorAll('.cn-band-col').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.dye === name));
    });

    document.querySelectorAll('.cn-product').forEach(function (p) {
      p.setAttribute('data-dim', String(p.dataset.dye !== name));
    });

    document.querySelectorAll('[data-shelf-note]').forEach(function (el) {
      var n = document.querySelectorAll('.cn-product[data-dye="' + name + '"]').length;
      el.textContent = n + (n === 1 ? ' piece in ' : ' pieces in ') + name;
    });
    document.querySelectorAll('[data-clear-filter]').forEach(function (el) { el.hidden = false; });

    document.querySelectorAll('[data-proof]').forEach(function (el) { paintProof(el, hex); });
    document.querySelectorAll('[data-proof-label]').forEach(function (el) {
      var count = el.dataset.proofLabel || '96';
      el.textContent = 'One dye. One recipe. ' + count + ' lengths of ' + name + '.';
    });
  };

  Conscience.clearDye = function () {
    Conscience.activeDye = null;
    document.documentElement.style.setProperty('--cn-accent', DEFAULT_ACCENT);
    document.querySelectorAll('.cn-band-col').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
    document.querySelectorAll('.cn-product').forEach(function (p) { p.setAttribute('data-dim', 'false'); });
    document.querySelectorAll('[data-shelf-note]').forEach(function (el) { el.textContent = 'Showing every piece'; });
    document.querySelectorAll('[data-clear-filter]').forEach(function (el) { el.hidden = true; });
  };

  window.Conscience = Conscience;

  /* ---------------- 1. the vat ---------------- */
  function initVat(scope) {
    scope.querySelectorAll('[data-vat]').forEach(function (vat) {
      if (vat.dataset.ready === 'true') return;
      vat.dataset.ready = 'true';

      var cap = vat.querySelector('[data-vat-cap]');
      var count = vat.querySelector('[data-vat-count]');
      var hint = vat.querySelector('[data-vat-hint]');
      var dips;
      try { dips = JSON.parse(vat.dataset.dips); } catch (e) { dips = []; }
      if (!dips.length) return;

      var i = 0;

      function dip() {
        if (i > dips.length) {
          i = 0;
          vat.style.background = 'var(--vat-green)';
          vat.classList.remove('is-oxidised');
          if (count) count.textContent = 'Dip 1';
          setTimeout(dip, 900);
          return;
        }
        var d = dips[Math.min(i, dips.length - 1)];
        if (i === 0) vat.classList.add('is-oxidised');
        vat.style.background = d.colour;
        if (cap) cap.textContent = d.caption;
        i++;
        if (count) count.textContent = 'Dip ' + Math.min(i, dips.length);
        if (hint) hint.textContent = i >= dips.length ? 'Tap to start a fresh cloth' : 'Tap to dip again';
      }

      setTimeout(dip, REDUCED ? 0 : 500);
      vat.addEventListener('click', dip);
    });
  }

  /* ---------------- 2. the dye band ---------------- */
  function initBand(scope) {
    scope.querySelectorAll('.cn-band-col').forEach(function (btn) {
      if (btn.dataset.ready === 'true') return;
      btn.dataset.ready = 'true';
      btn.addEventListener('click', function () {
        if (Conscience.activeDye === btn.dataset.dye) { Conscience.clearDye(); return; }
        Conscience.setDye(btn.dataset.dye, btn.dataset.hex);
      });
    });

    scope.querySelectorAll('[data-clear-filter]').forEach(function (btn) {
      if (btn.dataset.ready === 'true') return;
      btn.dataset.ready = 'true';
      btn.addEventListener('click', Conscience.clearDye);
    });
  }

  /* ---------------- 3. dye cards ---------------- */
  function initDyeCards(scope) {
    scope.querySelectorAll('.cn-dye').forEach(function (btn) {
      if (btn.dataset.ready === 'true') return;
      btn.dataset.ready = 'true';
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.closest('.cn-dyes').querySelectorAll('.cn-dye').forEach(function (b) {
          b.setAttribute('aria-expanded', 'false');
        });
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (!open && btn.dataset.dye && btn.dataset.hex) {
          Conscience.setDye(btn.dataset.dye, btn.dataset.hex);
        }
      });
    });
  }

  /* ---------------- 4. the variation proof ---------------- */
  function hexToHsl(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255,
        g = parseInt(hex.slice(3, 5), 16) / 255,
        b = parseInt(hex.slice(5, 7), 16) / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), h, s, l = (mx + mn) / 2, d = mx - mn;
    if (!d) { h = 0; s = 0; }
    else {
      s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
      h = mx === r ? ((g - b) / d + (g < b ? 6 : 0)) : mx === g ? ((b - r) / d + 2) : ((r - g) / d + 4);
      h *= 60;
    }
    return [h, s * 100, l * 100];
  }

  function paintProof(el, hex) {
    var hsl = hexToHsl(hex);
    var n = parseInt(el.dataset.count || '96', 10);
    el.innerHTML = '';
    for (var i = 0; i < n; i++) {
      var t = document.createElement('span');
      t.className = 'cn-proof-tile';
      var h = hsl[0] + (Math.random() * 10 - 5);
      var s = Math.max(4, Math.min(92, hsl[1] + (Math.random() * 22 - 11)));
      var l = Math.max(8, Math.min(88, hsl[2] + (Math.random() * 20 - 10)));
      t.style.background = 'hsl(' + h.toFixed(1) + ',' + s.toFixed(1) + '%,' + l.toFixed(1) + '%)';
      t.style.animationDelay = (i * 9) + 'ms';
      el.appendChild(t);
    }
  }

  function initProof(scope) {
    scope.querySelectorAll('[data-proof]').forEach(function (el) {
      paintProof(el, Conscience.activeDye ? currentHex() : DEFAULT_ACCENT);
    });
    scope.querySelectorAll('[data-proof-again]').forEach(function (btn) {
      if (btn.dataset.ready === 'true') return;
      btn.dataset.ready = 'true';
      btn.addEventListener('click', function () {
        document.querySelectorAll('[data-proof]').forEach(function (el) {
          paintProof(el, currentHex());
        });
      });
    });
  }

  function currentHex() {
    if (!Conscience.activeDye) return DEFAULT_ACCENT;
    var src = document.querySelector('.cn-band-col[data-dye="' + Conscience.activeDye + '"]');
    return src ? src.dataset.hex : DEFAULT_ACCENT;
  }

  /* ---------------- 5. material tabs ---------------- */
  function initTabs(scope) {
    scope.querySelectorAll('[data-tabs]').forEach(function (group) {
      if (group.dataset.ready === 'true') return;
      group.dataset.ready = 'true';
      var tabs = group.querySelectorAll('.cn-tab');
      tabs.forEach(function (t) {
        t.addEventListener('click', function () {
          tabs.forEach(function (x) { x.setAttribute('aria-selected', 'false'); });
          t.setAttribute('aria-selected', 'true');
          group.querySelectorAll('.cn-panel').forEach(function (p) {
            p.setAttribute('data-open', String(p.id === t.dataset.panel));
          });
        });
      });
    });
  }

  /* ---------------- 6. the loom ---------------- */
  function initLoom(scope) {
    scope.querySelectorAll('[data-weft]').forEach(function (g) {
      if (g.dataset.ready === 'true') return;
      g.dataset.ready = 'true';
      for (var y = 40; y <= 370; y += 22) {
        var l = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        l.setAttribute('x1', '30'); l.setAttribute('x2', '380');
        l.setAttribute('y1', y); l.setAttribute('y2', y);
        l.setAttribute('class', 'cn-weft');
        l.style.transitionDelay = ((y - 40) * 6) + 'ms';
        g.appendChild(l);
      }
    });
  }

  /* ---------------- 7. reveal on scroll ---------------- */
  function initReveal(scope) {
    var rises = scope.querySelectorAll('.cn-rise:not(.is-seen)');
    if (!('IntersectionObserver' in window) || REDUCED) {
      rises.forEach(function (el) {
        el.classList.add('is-seen');
        if (el.hasAttribute('data-loom')) el.classList.add('is-woven');
      });
      return;
    }
    var ro = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-seen');
        if (e.target.hasAttribute('data-loom')) e.target.classList.add('is-woven');
        ro.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    rises.forEach(function (el) { ro.observe(el); });
  }

  /* ---------------- boot ---------------- */
  function init(scope) {
    scope = scope || document;
    initVat(scope);
    initBand(scope);
    initDyeCards(scope);
    initProof(scope);
    initTabs(scope);
    initLoom(scope);
    initReveal(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Shopify theme editor: sections are re-rendered without a page reload */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });
})();
